<template>
  <b-modal
    v-model="visible"
    hide-header
    hide-footer
    centered
    size="md"
    body-class="p-0 login-modal-body"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <div class="login-modal-container" :class="theme === 'dark' ? 'theme-dark' : 'theme-light'">
      <!-- Toast提示组件 -->
      <div v-if="toast.show" class="toast-container">
        <div class="toast" :class="'toast-' + toast.type">
          <div class="toast-message">{{ toast.message }}</div>
        </div>
      </div>

      <div class="login-box-modal">
        <!-- 关闭按钮 -->
        <button type="button" class="close-btn" @click="visible = false">
          <b-icon icon="x" font-scale="1.5"></b-icon>
        </button>

        <div class="login-tabs">
          <div class="tab-item" :class="{ active: activeTab === 'email' }" @click="switchTab('email')">
            邮箱登录/注册
          </div>
          <div class="tab-item" :class="{ active: activeTab === 'wechat' }" @click="switchTab('wechat')">
            微信扫码登录
          </div>
        </div>

        <!-- 面板：微信扫码登录 -->
        <div class="tab-panel" v-if="activeTab === 'wechat'">
          <h4 class="login-title">微信登录</h4>
          <div>
            <div id="wx-qrcode-modal" class="qrcode-container"></div>
            <div class="wechat-tip">请使用微信扫一扫，完成授权登录。</div>
            <div v-if="isMobile" class="wechat-mobile-tip mt-2">
              <div class="mobile-tip-title">
                <svg class="tip-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>重要提示：</span>
              </div>
              <div class="mobile-tip-content">
                微信扫码登录需要打开摄像头直接扫码，请在手机端切换使用左侧<strong>“邮箱登录/注册”</strong>。
              </div>
            </div>
          </div>
        </div>

        <!-- 面板：邮箱登录/注册 -->
        <div class="tab-panel" v-else>
          <h4 class="login-title">{{ isLogin ? (isForgot ? '重置密码' : '邮箱登录') : '邮箱注册' }}</h4>
          
          <!-- 登录表单 -->
          <form v-if="isLogin && !isForgot" class="email-form" @submit.prevent="handleLogin">
            <div class="form-item">
              <input 
                type="email" 
                v-model="loginForm.email" 
                placeholder="请输入邮箱" 
                class="form-input"
                @input="errorMessage = ''"
                key="login-email"
                autocomplete="username"
              />
            </div>
            <div class="form-item">
              <input 
                type="password" 
                v-model="loginForm.password" 
                placeholder="请输入密码" 
                class="form-input"
                @keyup.enter="handleLogin"
                key="login-password"
                autocomplete="current-password"
              />
            </div>
            <div class="form-item remember-row d-flex justify-content-between align-items-center">
              <label class="remember-label">
                <input type="checkbox" v-model="rememberPassword" /> 记住密码
              </label>
            </div>
            <!-- 错误信息展示 -->
            <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
            <div class="form-item">
              <button type="submit" class="form-button" :disabled="loading">
                <span v-if="loading"><b-spinner small></b-spinner> 登录中...</span>
                <span v-else>登录</span>
              </button>
            </div>
            <div class="form-footer">
              <span @click="isForgot = true; forgotForm.email = loginForm.email" class="form-link" style="margin-right: 15px;">忘记密码？</span>
              <span @click="toggleAuthMode(false)" class="form-link">没有账号？去注册</span>
            </div>
          </form>

          <!-- 忘记密码表单 -->
          <div v-else-if="isLogin && isForgot" class="email-form">
            <div class="form-item">
              <input 
                type="email" 
                v-model="forgotForm.email" 
                placeholder="请输入邮箱" 
                class="form-input" 
                key="forgot-email" 
                @input="errorMessage = ''; forgotForm.code = ''" 
                autocomplete="username"
              />
            </div>
            <div class="form-item verification-code">
              <input 
                type="text" 
                v-model="forgotForm.code" 
                placeholder="请输入验证码" 
                class="form-input" 
                key="forgot-code" 
                @input="errorMessage = ''" 
                autocomplete="one-time-code" 
              />
              <button 
                @click="sendResetCode" 
                class="code-button"
                :class="{
                  'sending': sendingCode,
                  'countdown': cooldown > 0,
                  'resend': codeStatus === 'resend'
                }"
                :disabled="cooldown > 0 || sendingCode"
              >
                <span v-if="sendingCode">发送中...</span>
                <span v-else-if="cooldown > 0">{{ cooldown }}s</span>
                <span v-else-if="codeStatus === 'resend'">重新发送</span>
                <span v-else>发送验证码</span>
              </button>
            </div>
            <div class="form-item">
              <input 
                type="password" 
                v-model="forgotForm.password" 
                placeholder="请输入新密码" 
                class="form-input" 
                key="forgot-password" 
                autocomplete="new-password"
              />
            </div>
            <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
            <div class="form-item">
              <button type="button" class="form-button" @click="handleReset" :disabled="loading">
                <span v-if="loading"><b-spinner small></b-spinner> 重置中...</span>
                <span v-else>重置密码</span>
              </button>
            </div>
            <div class="form-footer">
              <span @click="isForgot = false" class="form-link">返回登录</span>
            </div>
          </div>

          <!-- 注册表单 -->
          <div v-else class="email-form">
            <div class="form-item">
              <input 
                type="email" 
                v-model="registerForm.email" 
                placeholder="请输入邮箱" 
                class="form-input"
                key="register-email"
                @input="errorMessage = ''; registerForm.code = ''"
                autocomplete="username"
              />
            </div>
            <div class="form-item verification-code">
              <input 
                type="text" 
                v-model="registerForm.code" 
                placeholder="请输入验证码" 
                class="form-input"
                key="register-code"
                autocomplete="one-time-code"
              />
              <button 
                @click="sendVerificationCode" 
                class="code-button"
                :class="{
                  'sending': sendingCode,
                  'countdown': cooldown > 0,
                  'resend': codeStatus === 'resend'
                }"
                :disabled="cooldown > 0 || sendingCode"
              >
                <span v-if="sendingCode">发送中...</span>
                <span v-else-if="cooldown > 0">{{ cooldown }}s</span>
                <span v-else-if="codeStatus === 'resend'">重新发送</span>
                <span v-else>获取验证码</span>
              </button>
            </div>
            <div class="form-item">
              <input 
                type="password" 
                v-model="registerForm.password" 
                placeholder="请输入密码" 
                class="form-input"
                key="register-password"
                autocomplete="new-password"
              />
            </div>
            <div class="form-item">
              <input 
                type="password" 
                v-model="registerForm.confirmPassword" 
                placeholder="请确认密码" 
                class="form-input"
                key="register-confirm-password"
                autocomplete="new-password"
              />
            </div>
            <div class="form-item">
              <input 
                type="text" 
                v-model="registerForm.nickname" 
                placeholder="请输入昵称" 
                class="form-input"
                @blur="checkNicknameExists"
                key="register-nickname"
                autocomplete="username"
              />
            </div>
            <div v-if="nicknameStatus==='taken'" class="error-banner">该昵称已被使用，请更换</div>
            <div v-else-if="nicknameStatus==='error'" class="error-banner">检查昵称失败，请稍后重试</div>
            <!-- 错误信息展示 -->
            <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
            <div class="form-item">
              <button @click="handleRegister" class="form-button" :disabled="loading">
                <span v-if="loading"><b-spinner small></b-spinner> 注册中...</span>
                <span v-else>注册并登录</span>
              </button>
            </div>
            <div class="form-footer">
              <span @click="toggleAuthMode(true)" class="form-link">已有账号？去登录</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LoginModal',
  data() {
    return {
      visible: false,
      theme: 'light',
      isLogin: true,
      isForgot: false,
      activeTab: 'email',
      wechatQrInited: false,
      
      loginForm: {
        email: '',
        password: ''
      },
      forgotForm: {
        email: '',
        code: '',
        password: ''
      },
      registerForm: {
        email: '',
        code: '',
        password: '',
        confirmPassword: '',
        nickname: ''
      },
      checkingNickname: false,
      nicknameStatus: '',
      rememberPassword: false,
      
      cooldown: 0,
      codeStatus: 'initial',
      sendingCode: false,
      errorMessage: '',
      loading: false,
      
      toast: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      }
    }
  },
  computed: {
    isWeChat() {
      return /micromessenger/i.test(navigator.userAgent.toLowerCase())
    },
    isMobile() {
      return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase())
    },
    platform() {
      if (this.isWeChat) return 'mp'
      return this.isMobile ? 'mobile_web' : 'pc_web'
    }
  },
  mounted() {
    this.readTheme()
    window.addEventListener('site-theme-changed', this.handleThemeChanged)
  },
  beforeDestroy() {
    window.removeEventListener('site-theme-changed', this.handleThemeChanged)
  },
  methods: {
    show() {
      this.visible = true
      this.isLogin = true
      this.isForgot = false
      this.activeTab = 'email'
      this.errorMessage = ''
      this.nicknameStatus = ''
      this.readLocal()
    },
    hide() {
      this.visible = false
    },
    readTheme() {
      try {
        const t = localStorage.getItem('site.theme')
        if (t) this.theme = t
      } catch (e) {
        console.warn('Failed to read theme', e)
      }
    },
    handleThemeChanged(e) {
      this.theme = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme') || 'light')
    },
    onModalShown() {
      this.readTheme()
      this.readLocal()
    },
    onModalHidden() {
      // Clear forms on close
      this.loginForm.password = ''
      this.registerForm.password = ''
      this.registerForm.confirmPassword = ''
      this.forgotForm.password = ''
      this.errorMessage = ''
    },
    readLocal() {
      try {
        const lastEmail = localStorage.getItem('last_login_email')
        if (lastEmail) {
          this.loginForm.email = lastEmail
        }
        const remember = localStorage.getItem('remember_password')
        this.rememberPassword = remember === '1' || remember === 'true'
        if (this.rememberPassword) {
          const lastPwd = localStorage.getItem('last_login_password') || ''
          this.loginForm.password = lastPwd
        }
      } catch (e) {
        console.warn('readLocal failed', e)
      }
    },
    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'wechat') {
        this.$nextTick(() => {
          this.initWeChatQr()
        })
      }
    },
    toggleAuthMode(isLogin) {
      if (!isLogin) {
        this.registerForm.email = this.loginForm.email
        this.registerForm.code = ''
        this.registerForm.password = ''
        this.registerForm.confirmPassword = ''
        this.registerForm.nickname = ''
      } else {
        this.loginForm.email = this.registerForm.email
        this.loginForm.password = ''
      }
      this.isLogin = isLogin
      this.errorMessage = ''
      this.nicknameStatus = ''
    },
    showToast(type, message, duration = 3000) {
      if (this.toast.timeout) {
        clearTimeout(this.toast.timeout)
      }
      this.toast.show = true
      this.toast.type = type
      this.toast.message = message
      this.toast.timeout = setTimeout(() => {
        this.toast.show = false
      }, duration)
    },
    async initWeChatQr() {
      try {
        await this.loadWxLoginScript()
        const appid = process.env.VUE_APP_WECHAT_QR_APPID
        if (!appid) {
          this.showToast('danger', '未配置微信网站应用APPID')
          return
        }
        const origin = window.location.origin
        const redirectPath = window.location.pathname
        const invite_code = this.$route.query.invite_code || localStorage.getItem('invite_code') || ''
        const redirectUri = encodeURIComponent(`${origin}${redirectPath}`)
        const state = invite_code || 'none'

        /* global WxLogin */
        new WxLogin({
          id: 'wx-qrcode-modal',
          appid,
          scope: 'snsapi_login',
          redirect_uri: redirectUri,
          state,
          style: this.theme === 'dark' ? 'black' : 'white',
          href: ''
        })
        this.wechatQrInited = true
      } catch (e) {
        console.error('Failed to init wechat qr', e)
        this.showToast('danger', '加载微信二维码失败')
      }
    },
    loadWxLoginScript() {
      return new Promise((resolve, reject) => {
        if (window.WxLogin) return resolve()
        const script = document.createElement('script')
        script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('wxLogin.js load failed'))
        document.body.appendChild(script)
      })
    },
    async handleLogin() {
      if (!this.loginForm.email || !this.loginForm.password) {
        this.errorMessage = '请输入邮箱和密码'
        return
      }
      this.loading = true
      this.errorMessage = ''
      
      try {
        const response = await axios.post('https://api.zizizaizai.com/v2/login/email/login', {
          email: this.loginForm.email,
          password: this.loginForm.password,
          platform: this.platform
        })
        
        if (response.data.code === 20000) {
          const d = response.data.data || {}
          const accessToken = d.access_token || d.token
          const refreshToken = d.refresh_token
          const userInfo = d.user_info || null
          
          if (accessToken) {
            localStorage.setItem('token', accessToken)
          }
          if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken)
          }
          if (userInfo) {
            localStorage.setItem('user_info', JSON.stringify(userInfo))
            window.dispatchEvent(new CustomEvent('user-login', { detail: { userInfo } }))
          }
          
          localStorage.setItem('last_login_email', this.loginForm.email)
          localStorage.setItem('remember_password', this.rememberPassword ? '1' : '0')
          if (this.rememberPassword) {
            localStorage.setItem('last_login_password', this.loginForm.password)
          } else {
            localStorage.removeItem('last_login_password')
          }
          
          this.showToast('success', '登录成功')
          this.$emit('success')
          this.visible = false
        } else {
          this.errorMessage = response.data.message || '登录失败'
        }
      } catch (error) {
        console.error('Login failed', error)
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    async sendVerificationCode() {
      if (this.sendingCode || this.cooldown > 0) return
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.registerForm.email)) {
        this.errorMessage = '请输入有效的邮箱地址'
        return
      }
      
      this.sendingCode = true
      this.errorMessage = ''
      try {
        const response = await axios.post('https://api.zizizaizai.com/v2/login/email/send_code', {
          email: this.registerForm.email
        })
        if (response.data.code === 20000 || response.data.code === 0) {
          this.showToast('success', response.data.message || '验证码发送成功')
          this.cooldown = 60
          this.codeStatus = 'countdown'
          const timer = setInterval(() => {
            this.cooldown--
            if (this.cooldown <= 0) {
              clearInterval(timer)
              this.codeStatus = 'resend'
            }
          }, 1000)
        } else {
          this.errorMessage = response.data.message || '验证码发送失败'
        }
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试'
      } finally {
        this.sendingCode = false
      }
    },
    async handleRegister() {
      if (!this.registerForm.email || !this.registerForm.code || 
          !this.registerForm.password || !this.registerForm.confirmPassword || 
          !this.registerForm.nickname) {
        this.errorMessage = '请填写完整注册信息'
        return
      }
      
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.errorMessage = '两次输入的密码不一致'
        return
      }
      if (this.nicknameStatus === 'taken') {
        this.errorMessage = '该昵称已被使用，请更换'
        return
      }
      
      this.loading = true
      this.errorMessage = ''
      try {
        const invite_code = localStorage.getItem('invite_code') || ''
        const response = await axios.post('https://api.zizizaizai.com/v2/login/email/register', {
          email: this.registerForm.email,
          code: this.registerForm.code,
          password: this.registerForm.password,
          nickname: this.registerForm.nickname,
          invite_code: invite_code,
          platform: this.platform
        })
        
        if (response.data.code === 20000) {
          this.showToast('success', '注册成功，正在登录...')
          // Auto login
          this.loginForm.email = this.registerForm.email
          this.loginForm.password = this.registerForm.password
          await this.handleLogin()
        } else {
          this.errorMessage = response.data.message || '注册失败'
        }
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    async checkNicknameExists() {
      const nick = (this.registerForm.nickname || '').trim()
      if (!nick) {
        this.nicknameStatus = ''
        return
      }
      this.checkingNickname = true
      this.nicknameStatus = ''
      try {
        const rs = await axios.get('https://api.zizizaizai.com/user/check_nickname', { params: { nickname: nick }, timeout: 3000 })
        const ok = rs && rs.data && (rs.data.code === 20000 || rs.data.code === 0)
        const exists = rs && rs.data && rs.data.data ? !!rs.data.data.exists : false
        if (ok) {
          this.nicknameStatus = exists ? 'taken' : 'available'
        } else {
          this.nicknameStatus = 'error'
        }
      } catch (e) {
        this.nicknameStatus = 'error'
      } finally {
        this.checkingNickname = false
      }
    },
    async sendResetCode() {
      if (this.sendingCode || this.cooldown > 0) return
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.forgotForm.email)) {
        this.errorMessage = '请输入有效的邮箱地址'
        return
      }
      this.sendingCode = true
      this.errorMessage = ''
      try {
        const rs = await axios.post('https://api.zizizaizai.com/v2/login/email/reset/send_code', { email: this.forgotForm.email })
        if (rs.data && (rs.data.code === 20000 || rs.data.code === 0)) {
          this.showToast('success', rs.data.message || '验证码已发送')
          this.cooldown = 60
          this.codeStatus = 'countdown'
          const timer = setInterval(() => {
            this.cooldown--
            if (this.cooldown <= 0) {
              clearInterval(timer)
              this.codeStatus = 'resend'
            }
          }, 1000)
        } else {
          this.errorMessage = (rs.data && rs.data.message) || '发送失败'
        }
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试'
      } finally {
        this.sendingCode = false
      }
    },
    async handleReset() {
      if (!this.forgotForm.email || !this.forgotForm.code || !this.forgotForm.password) {
        this.errorMessage = '请输入完整信息'
        return
      }
      this.loading = true
      this.errorMessage = ''
      try {
        const rs = await axios.post('https://api.zizizaizai.com/v2/login/email/reset/confirm', {
          email: this.forgotForm.email,
          code: this.forgotForm.code,
          password: this.forgotForm.password
        })
        if (rs.data && rs.data.code === 20000) {
          this.showToast('success', '密码已重置，请登录')
          this.isForgot = false
          this.isLogin = true
          this.loginForm.email = this.forgotForm.email
          this.loginForm.password = ''
        } else {
          this.errorMessage = (rs.data && rs.data.message) || '重置失败'
        }
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Scoped vars matching design criteria */
.login-modal-container {
  --bg-main: #111827;
  --bg-card: #1f2937;
  --border-color: #374151;
  --text-main: #f3f4f6;
  --text-muted: #9ca3af;
  --accent-color: #3b82f6;
  background-color: var(--bg-card);
  color: var(--text-main);
  border-radius: 8px;
  overflow: hidden;
}

.login-modal-container.theme-light {
  --bg-main: #f9fafb;
  --bg-card: #ffffff;
  --border-color: #e5e7eb;
  --text-main: #111827;
  --text-muted: #6b7280;
  --accent-color: #3b82f6;
}

.login-box-modal {
  padding: 30px;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  outline: none;
  transition: color 0.2s;
  padding: 0;
  z-index: 10;
}

.close-btn:hover {
  color: var(--text-main);
}

.login-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 20px;
  margin-top: 10px;
}

.tab-item {
  flex: 1;
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s;
}

.tab-item.active {
  color: var(--accent-color);
  border-bottom: 2px solid var(--accent-color);
}

.tab-item:hover {
  color: var(--accent-color);
}

.tab-panel {
  min-height: 280px;
}

.login-title {
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-main);
  text-align: center;
}

.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 0;
  background: white;
  border-radius: 4px;
  margin: 0 auto;
  width: 200px;
  height: 200px;
}

.wechat-tip {
  margin-top: 15px;
  color: var(--text-muted);
  font-size: 13px;
  text-align: center;
}

.wechat-mobile-tip {
  padding: 10px;
  background-color: rgba(235, 94, 40, 0.08);
  border: 1px solid rgba(235, 94, 40, 0.2);
  border-left: 4px solid #eb5e28;
  border-radius: 6px;
  text-align: left;
}

.mobile-tip-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 12px;
  color: #eb5e28;
  margin-bottom: 4px;
}

.mobile-tip-content {
  font-size: 11px;
  color: var(--text-muted);
  line-height: 1.5;
}

.email-form {
  text-align: left;
}

.form-item {
  margin-bottom: 16px;
  position: relative;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-card);
  border-radius: 4px;
  color: var(--text-main);
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.remember-row {
  font-size: 13px;
  color: var(--text-muted);
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.form-button {
  width: 100%;
  height: 38px;
  background-color: var(--accent-color);
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.form-button:hover:not(:disabled) {
  opacity: 0.9;
}

.form-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-footer {
  text-align: center;
  margin-top: 10px;
  font-size: 13px;
}

.form-link {
  color: var(--accent-color);
  cursor: pointer;
}

.form-link:hover {
  text-decoration: underline;
}

.verification-code {
  display: flex;
  gap: 8px;
}

.verification-code .form-input {
  flex: 1;
}

.code-button {
  min-width: 100px;
  height: 38px;
  background-color: var(--accent-color);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 13px;
  transition: opacity 0.2s;
}

.code-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.code-button.countdown {
  background-color: #6c757d;
}

/* Toast提示样式 */
.toast-container {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.toast {
  padding: 8px 16px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.3s ease;
  min-width: 200px;
  text-align: center;
}

.toast-success {
  background-color: #d4edda;
  color: #155724;
  border-left: 4px solid #28a745;
}

.toast-danger {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.error-banner {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

::v-deep(.impowerBox) {
  width: 180px !important;
  margin: 0 auto !important;
}

::v-deep(.impowerBox .qrcode) {
  width: 150px !important;
  height: 150px !important;
}

::v-deep(.impowerBox .title), ::v-deep(.impowerBox .info) {
  display: none !important;
}
</style>
